import { useEffect, useRef } from 'react'
import { useText } from '../i18n.js'

/**
 * Schlichter modaler Rahmen. Bewusst kein `<dialog>`-Element: dessen
 * Top-Layer-Verhalten kollidiert mit den globalen Tastatur-Shortcuts des
 * Canvas, und wir brauchen ohnehin nur Overlay plus Fokusfalle.
 */
export function Dialog({
  title,
  onClose,
  children,
}: {
  title: string
  onClose: () => void
  children: React.ReactNode
}): React.ReactElement {
  const t = useText()
  const panelRef = useRef<HTMLDivElement>(null)

  /*
   * `onClose` kommt als Inline-Funktion herein und ist damit bei jedem Render
   * neu. Über eine Referenz gehalten, bleiben die Effekte unten stabil.
   */
  const close = useRef(onClose)
  close.current = onClose

  /*
   * Der Anfangsfokus wird **einmal** gesetzt, nicht bei jedem Render.
   *
   * Hing er an `onClose`, lief er bei jeder fremden Cursorbewegung erneut —
   * das Board rendert dabei neu — und riss ein gerade geöffnetes Auswahlfeld
   * wieder zu. Genau so ein Fall war hier: Das Schriftmenü schloss sich nach
   * etwa einer Sekunde von selbst.
   */
  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('input, button, a')?.focus()
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        close.current()
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [])

  return (
    <div className="overlay" onPointerDown={() => close.current()}>
      <div
        className="dialog"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <header className="dialog-head">
          <h3>{title}</h3>
          <button
            className="button ghost icon"
            onClick={() => close.current()}
            aria-label={t('common.close')}
          >
            ×
          </button>
        </header>
        <div className="dialog-body">{children}</div>
      </div>
    </div>
  )
}
